import React, { useState, useEffect } from 'react';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Heart,
  Share2,
  ShoppingCart,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useCart } from '@context/CartContext';
import { useAuth } from '@context/AuthContext';
import { useFavorites } from '@context/FavoritesContext';
import { formatCurrency, formatDate } from '@utils/helpers';
import { PRICING } from '@utils/constants';
import Modal from '@components/common/Modal';
import Button from '@components/common/Button';
import type { Photo } from '@/types';

interface PhotoModalProps {
  photo: Photo | null;
  photos: Photo[];
  isOpen: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

const PhotoModal: React.FC<PhotoModalProps> = ({
  photo,
  photos,
  isOpen,
  onClose,
  onNext,
  onPrevious,
}) => {
  const [selectedResolution, setSelectedResolution] = useState<'web' | 'print' | 'full'>('web');
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageTransform, setImageTransform] = useState({ scale: 1, rotation: 0 });
  const { addToCart, isInCart } = useCart();
  const { user } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();

  const currentPhotoIndex = photos.findIndex(p => p.id === photo?.id);
  const hasNext = currentPhotoIndex < photos.length - 1;
  const hasPrevious = currentPhotoIndex > 0;

  // Reset state when photo changes
  useEffect(() => {
    setIsZoomed(false);
    setImageTransform({ scale: 1, rotation: 0 });
    setSelectedResolution('web');
  }, [photo?.id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowRight':
          if (hasNext && onNext) onNext();
          break;
        case 'ArrowLeft':
          if (hasPrevious && onPrevious) onPrevious();
          break;
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          resetZoom();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, hasNext, hasPrevious, onNext, onPrevious, onClose]);

  if (!photo) return null;

  const inCart = isInCart(photo.id, selectedResolution);
  const resolution = PRICING.resolutions[selectedResolution];
  const photoPrice = resolution.price;
  const isFavorited = isFavorite(photo.id);

  const handleAddToCart = () => {
    addToCart({
      photoId: photo.id,
      eventId: photo.eventId,
      title: photo.title || `Photo ${photo.id}`,
      thumbnail: photo.thumbnail,
      resolution: selectedResolution,
      price: photoPrice,
      quantity: 1,
    });
  };

  const handleToggleFavorite = () => {
    toggleFavorite(photo);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: photo.title || 'Photo from PixelShare',
          url: window.location.href,
        });
      } catch (error) {
        // Handle share error
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  const handleZoomIn = () => {
    setImageTransform(prev => ({
      ...prev,
      scale: Math.min(prev.scale * 1.2, 3),
    }));
    setIsZoomed(true);
  };

  const handleZoomOut = () => {
    setImageTransform(prev => ({
      ...prev,
      scale: Math.max(prev.scale / 1.2, 0.5),
    }));
  };

  const resetZoom = () => {
    setImageTransform({ scale: 1, rotation: 0 });
    setIsZoomed(false);
  };

  const handleRotate = () => {
    setImageTransform(prev => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360,
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      showCloseButton={false}
      className="max-w-none max-h-none h-screen m-0 rounded-none flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black bg-opacity-90 text-white">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-medium">{photo.title || `Photo ${photo.id}`}</h2>
          {currentPhotoIndex >= 0 && (
            <span className="text-sm text-gray-300">
              {currentPhotoIndex + 1} of {photos.length}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Zoom controls */}
          <Button
            size="small"
            variant="ghost"
            className="text-white hover:bg-white hover:bg-opacity-20"
            onClick={handleZoomOut}
            icon={<ZoomOut size={16} />}
            disabled={imageTransform.scale <= 0.5}
          />
          <Button
            size="small"
            variant="ghost"
            className="text-white hover:bg-white hover:bg-opacity-20"
            onClick={resetZoom}
            disabled={imageTransform.scale === 1 && imageTransform.rotation === 0}
          >
            {Math.round(imageTransform.scale * 100)}%
          </Button>
          <Button
            size="small"
            variant="ghost"
            className="text-white hover:bg-white hover:bg-opacity-20"
            onClick={handleZoomIn}
            icon={<ZoomIn size={16} />}
            disabled={imageTransform.scale >= 3}
          />
          <Button
            size="small"
            variant="ghost"
            className="text-white hover:bg-white hover:bg-opacity-20"
            onClick={handleRotate}
            icon={<RotateCcw size={16} />}
          />

          {/* Action buttons */}
          {user && (
            <Button
              size="small"
              variant="ghost"
              className="text-white hover:bg-white hover:bg-opacity-20"
              onClick={handleToggleFavorite}
              icon={<Heart size={16} className={isFavorited ? 'fill-red-500 text-red-500' : ''} />}
            />
          )}
          <Button
            size="small"
            variant="ghost"
            className="text-white hover:bg-white hover:bg-opacity-20"
            onClick={handleShare}
            icon={<Share2 size={16} />}
          />
          <Button
            size="small"
            variant="ghost"
            className="text-white hover:bg-white hover:bg-opacity-20"
            onClick={onClose}
            icon={<X size={16} />}
          />
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 min-h-0">
        {/* Image area */}
        <div className="flex-1 relative bg-black flex items-center justify-center">
          {/* Navigation buttons */}
          {hasPrevious && (
            <button
              onClick={onPrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {hasNext && (
            <button
              onClick={onNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all"
            >
              <ChevronRight size={24} />
            </button>
          )}

          {/* Main image */}
          <div
            className="max-w-full max-h-full flex items-center justify-center"
            style={{
              cursor: isZoomed ? 'grab' : 'zoom-in',
            }}
          >
            <img
              src={photo.webUrl}
              alt={photo.title || `Photo ${photo.id}`}
              className="max-w-full max-h-full object-contain transition-transform duration-200"
              style={{
                transform: `scale(${imageTransform.scale}) rotate(${imageTransform.rotation}deg)`,
              }}
              onClick={() => !isZoomed && handleZoomIn()}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80 bg-white flex flex-col">
          {/* Photo details */}
          <div className="p-6 border-b">
            <h3 className="font-medium text-gray-900 mb-2">{photo.title || `Photo ${photo.id}`}</h3>
            {photo.description && <p className="text-sm text-gray-600 mb-4">{photo.description}</p>}

            {/* Metadata */}
            <div className="space-y-2 text-sm text-gray-500">
              <div>Date: {formatDate(photo.createdAt)}</div>
              {photo.metadata && (
                <>
                  <div>
                    Resolution: {photo.metadata.width} × {photo.metadata.height}
                  </div>
                  <div>File size: {Math.round(photo.metadata.fileSize / 1024)} KB</div>
                  {photo.metadata.camera && <div>Camera: {photo.metadata.camera}</div>}
                  {photo.metadata.lens && <div>Lens: {photo.metadata.lens}</div>}
                  {photo.metadata.settings && <div>Settings: {photo.metadata.settings}</div>}
                </>
              )}
            </div>

            {/* Tags */}
            {photo.tags && photo.tags.length > 0 && (
              <div className="mt-4">
                <div className="flex flex-wrap gap-2">
                  {photo.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Purchase options */}
          <div className="p-6 flex-1">
            <h4 className="font-medium text-gray-900 mb-4">Purchase Options</h4>

            <div className="space-y-3 mb-6">
              {Object.values(PRICING.resolutions).map(res => (
                <label
                  key={res.key}
                  className={clsx(
                    'flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors',
                    selectedResolution === res.key
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <input
                    type="radio"
                    name="resolution"
                    value={res.key}
                    checked={selectedResolution === res.key}
                    onChange={e =>
                      setSelectedResolution(e.target.value as typeof selectedResolution)
                    }
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{res.name}</span>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(res.price)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{res.description}</p>
                    <p className="text-xs text-gray-500 mt-1">{res.dimensions}</p>
                  </div>
                </label>
              ))}
            </div>

            <Button
              variant={inCart ? 'secondary' : 'primary'}
              size="large"
              className="w-full"
              onClick={handleAddToCart}
              disabled={inCart}
              icon={inCart ? <Download size={16} /> : <ShoppingCart size={16} />}
            >
              {inCart ? 'Added to Cart' : `Add to Cart - ${formatCurrency(photoPrice)}`}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PhotoModal;
